import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useMatches,
  useRouteError
} from "@remix-run/react";
import type { LinksFunction, SerializeFrom } from "@remix-run/node"; // or cloudflare/deno
import { alphabetical, useGtm } from "./library/utilities";

import fonts from "~/fonts.css?url";
import page from "~/page.css?url";
import styles from "~/tailwind.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
  { rel: "stylesheet", href: fonts },
  { rel: "stylesheet", href: page },
  { rel: "icon", type: "image/png", href: "/favicon.webp" }
];

export const useRootLoaderData = () => {
  const [root] = useMatches();
  return root?.data;
};

export const loader = async () => {
  const apiKey = process.env.API_KEY;

  if (!apiKey) {
    throw new Error("Missing API_KEY environment variable");
  }

  const url = new URL(
    "https://welten.my.salesforce-sites.com/services/apexrest/byner/BYNER_WEBSITE_REST_API/v2/getJobs"
  );

  url.searchParams.set("internal_channel", "CustomerJobs");
  url.searchParams.set("start", "0");
  url.searchParams.set("apiKey", apiKey);

  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error(`Failed to fetch jobs: ${response.status} ${response.statusText}`);
  }

  const { results: jobs } = await response.json();

  const locaties = jobs.map((job: any) => job?.job?.byner__Job_location__c);
  const expertisegebieden = jobs.map((job: any) => job?.job?.Vakgebied__c);
  const opdrachtgevers = jobs.map((job: any) => job?.account?.Name);

  return {
    jobs: jobs,
    filters: {
      locaties: [...new Set(locaties.filter(Boolean))].sort(alphabetical),
      expertisegebieden: [...new Set(expertisegebieden.filter(Boolean))].sort(alphabetical),
      opdrachtgevers: [...new Set(opdrachtgevers.filter(Boolean))].sort(alphabetical),
    }
  };
};

export type RootLoader = typeof loader


export function Layout({ children }: { children: React.ReactNode }) {
  useGtm();

  return (
    <html lang="nl-NL">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <noscript
          dangerouslySetInnerHTML={{
            __html: `
              <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-MNMKR9RK" 
              height="0" width="0" style="display: none; visibility: hidden;"></iframe>
            `,
          }}
        />

        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  return <div className="u-p-8 u-border u-border-dashed u-m-8 u-inline-block">Something went wrong</div>;
};

export default function App() {
  return <Outlet />;
}